import axios from 'axios';
import { TomTomConfig } from '@tomtom-org/maps-sdk/core';
import { TomTomMap } from '@tomtom-org/maps-sdk/map';
import { API_BASE_URL } from "../../../../config/apiConfig";
// Import the actual functions from services
import { calculateRoute, search, geocode, reverseGeocode } from '@tomtom-org/maps-sdk/services';

const BASE_URL = `${API_BASE_URL}/explore/places`;

// Add these constants
const DEFAULT_CENTER = [-74.5, 40]; // [longitude, latitude]
const DEFAULT_ZOOM = 12;

class ExploreService {
  constructor(token) {
    // Initialize TomTom SDK configuration FIRST
    this.tomtomKey = process.env.REACT_APP_TOMTOM_API_KEY || 'zBflIBzVLyiUGLT7DMben73c2UPfNs1x';
    
    if (!this.tomtomKey || this.tomtomKey === 'YOUR_TOMTOM_API_KEY') {
      console.warn('⚠️ TomTom API key not found. Please set REACT_APP_TOMTOM_API_KEY in .env file');
    }
    
    // Initialize TomTom SDK config
    try {
      TomTomConfig.instance.put({ 
        apiKey: this.tomtomKey, 
        language: 'en-US'
      });
    } catch (error) {
      console.error('Failed to initialize TomTom config:', error);
    }
    
    this.api = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });
    
    if (token) {
      this.setToken(token);
    }
  }

  setToken(token) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Test API key validity
   */
  async testAPIKey() {
    try {
      // Test with a simple search query
      const response = await axios.get(
        'https://api.tomtom.com/search/2/search/.json',
        {
          params: {
            key: this.tomtomKey,
            query: 'new york',
            limit: 1
          }
        }
      );
      return response.status === 200;
    } catch (error) {
      console.error('API key test failed:', error);
      return false;
    }
  }

  /**
   * Initialize TomTom Map with new SDK
   */
  async initTomTomMap(containerId, options = {}) {
    try {
      // Ensure container exists
      if (!containerId) {
        throw new Error('Container ID is required');
      }

      console.log('Initializing map with container:', containerId);
      
      const map = new TomTomMap({
        container: containerId,
        center: options.center || DEFAULT_CENTER,
        zoom: options.zoom || DEFAULT_ZOOM,
        style: 'tomtom/basic-main',
        bearing: 0,
        pitch: 0,
        attributionControl: true,
        ...options
      });

      return map;
    } catch (error) {
      console.error('Failed to initialize TomTom map:', error);
      throw error;
    }
  }

  /**
   * Generate TomTom Map URL for sharing
   */
  generateMapUrl(latitude, longitude, zoom = 15, placeName = '') {
    const baseUrl = 'https://api.tomtom.com/map/1/staticimage';
    const markers = `pin-s-l+000000(${longitude},${latitude})`;
    
    const params = new URLSearchParams({
      key: this.tomtomKey,
      center: `${longitude},${latitude}`,
      zoom: zoom.toString(),
      width: '800',
      height: '600',
      format: 'png',
      layer: 'basic',
      style: 'main',
      view: 'Unified',
      markers: markers
    });

    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Get route between two points using new SDK services
   */
  async getRoute(fromLat, fromLng, toLat, toLng, travelMode = 'car') {
    try {
      // Use calculateRoute directly (not routing().calculateRoute())
      const response = await calculateRoute({
        locations: [
          { lat: fromLat, lon: fromLng },
          { lat: toLat, lon: toLng }
        ],
        travelMode: travelMode,
        routeType: 'fastest',
        traffic: true,
        instructionsType: 'text',
        language: 'en-US'
      });

      if (response.routes && response.routes.length > 0) {
        return response.routes[0];
      }
      return null;
    } catch (error) {
      console.error('Route calculation error with new SDK:', error);
      // Fallback to direct API call
      return await this.getRouteViaAPI(fromLat, fromLng, toLat, toLng, travelMode);
    }
  }

  /**
   * Alternative: Get route using TomTom API directly (without SDK)
   */
  async getRouteViaAPI(fromLat, fromLng, toLat, toLng, travelMode = 'car') {
    try {
      const response = await axios.get(
        'https://api.tomtom.com/routing/1/calculateRoute/json',
        {
          params: {
            key: this.tomtomKey,
            travelMode: travelMode,
            routeType: 'fastest',
            traffic: 'true',
            avoid: 'unpavedRoads',
            vehicleMaxSpeed: 120,
            vehicleCommercial: false,
            vehicleEngineType: 'combustion',
            instructionsType: 'text',
            language: 'en-US',
            vehicleWeight: 1600,
            departAt: 'now',
            computeBestOrder: false,
            computeTravelTimeFor: 'all',
            routeRepresentation: 'polyline',
            compute: 'true',
            routeRepresentationOptions: 'polyline',
            sectionType: ['carTrain']
          }
        }
      );

      if (response.data.routes && response.data.routes.length > 0) {
        return response.data.routes[0];
      }
      return null;
    } catch (error) {
      console.error('TomTom API route calculation error:', error);
      return null;
    }
  }

  /**
   * Search for places using new SDK
   */
  async searchPlaces(query, options = {}) {
    try {
      // Use search directly
      const response = await search({
        query: query,
        limit: options.limit || 10,
        countrySet: options.countrySet || 'US,CA,MX,GB',
        typeahead: options.typeahead !== false,
        view: 'Unified'
      });

      return response.results || [];
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  /**
   * Geocode address using new SDK
   */
  async geocodeWithTomTom(address) {
    try {
      // Use geocode directly
      const response = await geocode({
        query: address,
        limit: 1
      });

      if (response.results && response.results.length > 0) {
        const result = response.results[0];
        return {
          latitude: result.position.lat,
          longitude: result.position.lon,
          address: result.address.freeformAddress,
          city: result.address.municipality,
          country: result.address.country,
          countryCode: result.address.countryCode
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error with new SDK:', error);
      // Fallback to API call
      return await this.geocodeViaAPI(address);
    }
  }

  /**
   * Alternative: Geocode using TomTom API directly
   */
  async geocodeViaAPI(address) {
    try {
      const response = await axios.get(
        'https://api.tomtom.com/search/2/geocode/.json',
        {
          params: {
            key: this.tomtomKey,
            query: address,
            limit: 1,
            typeahead: false,
            countrySet: 'US,CA,MX,GB,AU,NZ',
            view: 'Unified'
          }
        }
      );

      if (response.data.results && response.data.results.length > 0) {
        const result = response.data.results[0];
        return {
          latitude: result.position.lat,
          longitude: result.position.lon,
          address: result.address.freeformAddress,
          city: result.address.municipality,
          country: result.address.country,
          countryCode: result.address.countryCode
        };
      }
      return null;
    } catch (error) {
      console.error('TomTom API geocoding error:', error);
      return null;
    }
  }

  /**
   * Reverse geocode using new SDK
   */
  async reverseGeocodeWithTomTom(latitude, longitude) {
    try {
      // Use reverseGeocode directly
      const response = await reverseGeocode({
        position: { lat: latitude, lon: longitude },
        radius: 100
      });

      if (response.addresses && response.addresses.length > 0) {
        const address = response.addresses[0].address;
        return {
          address: address.freeformAddress,
          street: address.streetName,
          city: address.municipality,
          country: address.country,
          countryCode: address.countryCode,
          postalCode: address.postalCode
        };
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error with new SDK:', error);
      return await this.reverseGeocodeViaAPI(latitude, longitude);
    }
  }

  /**
   * Alternative: Reverse geocode using TomTom API directly
   */
  async reverseGeocodeViaAPI(latitude, longitude) {
    try {
      const response = await axios.get(
        'https://api.tomtom.com/search/2/reverseGeocode/.json',
        {
          params: {
            key: this.tomtomKey,
            position: `${latitude},${longitude}`,
            radius: 100,
            language: 'en-US',
            view: 'Unified',
            allowFreeformNewline: false,
            entityTypeSet: 'Municipality'
          }
        }
      );

      if (response.data.addresses && response.data.addresses.length > 0) {
        const address = response.data.addresses[0].address;
        return {
          address: address.freeformAddress,
          street: address.streetName,
          city: address.municipality,
          country: address.country,
          countryCode: address.countryCode,
          postalCode: address.postalCode
        };
      }
      return null;
    } catch (error) {
      console.error('TomTom API reverse geocoding error:', error);
      return null;
    }
  }

  // ... rest of your methods remain the same ...
  // (keep the addMarkersToMap, drawRouteOnMap, and other existing methods)

  /**
   * Add markers to map using new SDK
   */
  addMarkersToMap(map, places) {
    if (!map || !places) return [];

    const markers = [];
    
    places.forEach((place, index) => {
      if (place.coordinates?.latitude && place.coordinates?.longitude) {
        try {
          // Create marker element
          const markerElement = document.createElement('div');
          markerElement.className = 'custom-marker';
          markerElement.innerHTML = place.icon || '📍';
          markerElement.style.cssText = `
            font-size: 24px;
            cursor: pointer;
            transform: translate(-50%, -100%);
          `;

          // Create and add marker
          const marker = new TomTomMap.Marker({
            element: markerElement,
            anchor: 'bottom'
          })
            .setLngLat([place.coordinates.longitude, place.coordinates.latitude])
            .addTo(map);

          // Create popup
          const popup = new TomTomMap.Popup({ offset: 25 })
            .setHTML(this.createPlacePopupContent(place));

          marker.setPopup(popup);
          markers.push(marker);
        } catch (error) {
          console.error('Error adding marker:', error);
        }
      }
    });

    return markers;
  }

  /**
   * Create popup content for places
   */
  createPlacePopupContent(place) {
    const jsonPlace = JSON.stringify(place).replace(/'/g, "\\'");
    
    return `
      <div style="padding: 12px; min-width: 200px;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="font-size: 20px;">${place.icon || '📍'}</span>
          <h4 style="margin: 0; font-weight: 600;">${place.name}</h4>
        </div>
        <div style="margin-bottom: 12px;">
          ${place.address ? `<p style="margin: 4px 0; font-size: 14px; color: #666;">${place.address}</p>` : ''}
          ${place.distance ? `<p style="margin: 4px 0; font-size: 14px; color: #666;">Distance: ${place.distance}</p>` : ''}
          ${place.rating ? `<p style="margin: 4px 0; font-size: 14px; color: #666;">Rating: ${place.rating}/5 ⭐</p>` : ''}
        </div>
        <div style="display: flex; gap: 8px;">
          <button 
            onclick="window.dispatchEvent(new CustomEvent('getDirections', { detail: ${jsonPlace} }))"
            style="flex: 1; padding: 6px 12px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
          >
            Directions
          </button>
          <button 
            onclick="window.dispatchEvent(new CustomEvent('shareLocation', { detail: ${jsonPlace} }))"
            style="flex: 1; padding: 6px 12px; background: #8b5cf6; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
          >
            Share
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Draw route on map using new SDK
   */
  drawRouteOnMap(map, routeData) {
    if (!map || !routeData) return null;

    try {
      // Remove existing route if any
      if (map.getSource('route')) {
        map.removeLayer('route');
        map.removeSource('route');
      }

      // Prepare route coordinates
      const coordinates = routeData.legs[0].points.map(point => [
        point.longitude, 
        point.latitude
      ]);

      // Add route as GeoJSON
      map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates
          }
        }
      });

      map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#4F46E5',
          'line-width': 4,
          'line-opacity': 0.7
        }
      });

      return 'route';
    } catch (error) {
      console.error('Error drawing route:', error);
      return null;
    }
  }

// Add to your exploreService.js
generatePublicShareUrl(latitude, longitude, placeName = '') {
  const baseUrl = 'https://maps.tomtom.com';
  const params = new URLSearchParams({
    lat: latitude.toString(),
    lon: longitude.toString(),
    zoom: '15',
    layer: 'basic',
    style: 'main',
    traffic: 'true'
  });

  let url = `${baseUrl}?${params.toString()}`;
  
  if (placeName) {
    url += `&name=${encodeURIComponent(placeName)}`;
  }

  return url;
}


  /**
   * Generate shareable location link
   */
  generateShareableLink(latitude, longitude, placeName = '') {
    const baseUrl = 'https://maps.tomtom.com';
    const params = new URLSearchParams({
      lat: latitude.toString(),
      lon: longitude.toString(),
      zoom: '15',
      layer: 'basic',
      style: 'main'
    });

    let url = `${baseUrl}?${params.toString()}`;
    
    if (placeName) {
      url += `&name=${encodeURIComponent(placeName)}`;
    }

    return url;
  }

  /**
   * Generate directions URL
   */
  generateDirectionsUrl(fromLat, fromLng, toLat, toLng, travelMode = 'car') {
    const modes = {
      car: 'car',
      pedestrian: 'pedestrian',
      bicycle: 'bicycle',
      motorcycle: 'motorcycle',
      truck: 'truck',
      bus: 'bus'
    };

    const mode = modes[travelMode] || 'car';
    
    return `https://maps.tomtom.com/navigation/directions/?from=${fromLat},${fromLng}&to=${toLat},${toLng}&mode=${mode}`;
  }

  /**
   * Get static map image with route
   */
  generateRouteMapUrl(routePoints, width = 800, height = 600) {
    const baseUrl = 'https://api.tomtom.com/map/1/staticimage';
    
    // Format route path
    const path = routePoints.map(point => `${point.lng},${point.lat}`).join(':');
    
    const params = new URLSearchParams({
      key: this.tomtomKey,
      width: width.toString(),
      height: height.toString(),
      format: 'png',
      layer: 'basic',
      style: 'main',
      view: 'Unified',
      path: `lc:0000FF|lw:6|${path}`
    });

    return `${baseUrl}?${params.toString()}`;
  }

  // Your existing API methods
  async getExploreCategories() {
    try {
      const response = await this.api.get('/explore/categories');
      console.log('Explore categories response:', response.data);
      return Array.isArray(response.data) ? response.data : response.data.categories || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async searchNearbyPlaces(params) {
    try {
      const response = await this.api.post('', params);
      return response.data;
    } catch (error) {
      console.error('Error searching places:', error);
      throw error;
    }
  }

  async searchNearMe(params) {
    try {
      const response = await this.api.post('/near-me', params);
      return response.data;
    } catch (error) {
      console.error('Error searching near me:', error);
      throw error;
    }
  }

  async explorePlaces(params) {
    try {
      const response = await this.api.post('/explore', params);
      return response.data;
    } catch (error) {
      console.error('Error exploring places:', error);
      throw error;
    }
  }

  async reverseGeocode(latitude, longitude) {
    try {
      const response = await this.api.post('/reverse-geocode', {
        latitude,
        longitude
      });
      return response.data;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      throw error;
    }
  }

  /**
   * Geocode city name to get coordinates
   */
  async getCityCoordinates(city, country = null) {
    try {
      let query = city;
      if (country) {
        query = `${city}, ${country}`;
      }

      const response = await axios.get(
        'https://nominatim.openstreetmap.org/search',
        {
          params: {
            q: query,
            format: 'json',
            limit: 1,
            addressdetails: 1
          },
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'TheFrondApp/1.0'
          }
        }
      );

      if (response.data && response.data.length > 0) {
        const result = response.data[0];
        return {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
          city: city,
          country: country || result.address?.country,
          displayName: result.display_name,
          address: result.address,
          source: 'openstreetmap'
        };
      }
      
      throw new Error('City not found');
    } catch (error) {
      console.error('OpenStreetMap geocoding failed:', error);
      throw new Error(`Unable to find coordinates for "${city}"${country ? `, ${country}` : ''}`);
    }
  }

  /**
   * Get coordinates for country
   */
  async getCountryCoordinates(country) {
    try {
      const response = await axios.get(
        'https://nominatim.openstreetmap.org/search',
        {
          params: {
            country: country,
            format: 'json',
            limit: 1,
            addressdetails: 1
          },
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'TheFrondApp/1.0'
          }
        }
      );

      if (response.data && response.data.length > 0) {
        const result = response.data[0];
        return {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
          country: result.address?.country || country,
          displayName: result.display_name,
          capital: result.address?.capital || null,
          source: 'openstreetmap'
        };
      }
      
      throw new Error('Country not found');
    } catch (error) {
      console.error('Country geocoding failed:', error);
      throw new Error(`Unable to find coordinates for "${country}"`);
    }
  }

  /**
   * Validate coordinates
   */
  validateCoordinates(latitude, longitude) {
    if (latitude === undefined || longitude === undefined) {
      return { isValid: false, error: 'Coordinates are required' };
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      return { isValid: false, error: 'Invalid coordinate format' };
    }

    if (lat < -90 || lat > 90) {
      return { isValid: false, error: 'Latitude must be between -90 and 90' };
    }

    if (lng < -180 || lng > 180) {
      return { isValid: false, error: 'Longitude must be between -180 and 180' };
    }

    return { isValid: true, latitude: lat, longitude: lng };
  }
}

export const exploreService = new ExploreService();
export const createExploreService = (token) => {
  return new ExploreService(token);
};